import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";

export default function GamingFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [selectedStream, setSelectedStream] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedGame] = useState("all");

  const categories = [
    { id: "all", name: "Todos", icon: "🎮" },
    { id: "stream", name: "Lives", icon: "🔴" },
    { id: "clip", name: "Clips", icon: "✂️" },
    { id: "highlight", name: "Highlights", icon: "⭐" },
    { id: "walkthrough", name: "Gameplay", icon: "🎯" },
    { id: "review", name: "Reviews", icon: "📝" },
    { id: "esports", name: "E-Sports", icon: "🏆" },
  ];

  const fetchPosts = useCallback(async (pageNum = 1) => {
    try {
      const categoryParam = category !== "all" ? `&streamType=${category}` : "";
      const gameParam = selectedGame !== "all" ? `&game=${selectedGame}` : "";
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/gaming?page=${pageNum}&limit=12${categoryParam}${gameParam}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
      }
      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Gaming feed:", error);
      setLoading(false);
    }
  }, [category, selectedGame, user.token]);

  useEffect(() => {
    setPage(1);
    fetchPosts(1);
  }, [category, selectedGame, fetchPosts]);

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const formatViews = (num) => {
    if (!num) return "0";
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  if (loading && posts.length === 0) {
    return (
      <div className="gaming_feed_loading">
        <div className="gaming_loader"></div>
        <span>Carregando conteúdo...</span>
      </div>
    );
  }

  return (
    <div className="gaming_feed">
      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}
            label: "Jogo",
            value: selectedGame,
            onChange: setSelectedGame,
            options: games,
          }
        ]}
      />

      {/* Main Content */}
      <main className="gaming_main">
        {/* Live Now Section */}
        {category === "all" && posts.some((p) => p.gamingData?.isLive) && (
          <section className="gaming_live_section">
            <div className="gaming_section_header">
              <h2>
                <span className="gaming_live_dot"></span>
                Ao Vivo Agora
              </h2>
            </div>
            <div className="gaming_live_grid">
              {posts
                .filter((p) => p.gamingData?.isLive)
                .slice(0, 4)
                .map((post) => (
                  <div
                    key={post._id}
                    className="gaming_live_card"
                    onClick={() => setSelectedStream(post)}
                  >
                    <div className="gaming_live_thumbnail">
                      {post.videos?.[0] ? (
                        <video src={post.videos[0].url || post.videos[0]} muted />
                      ) : post.images?.[0] ? (
                        <img src={post.images[0].url || post.images[0]} alt="" />
                      ) : (
                        <div className="gaming_no_thumb">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                            <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                          </svg>
                        </div>
                      )}
                      <div className="gaming_live_badge">AO VIVO</div>
                      <div className="gaming_viewers">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        </svg>
                        {formatViews(post.gamingData?.viewers || Math.floor(Math.random() * 1000))}
                      </div>
                    </div>
                    <div className="gaming_live_info">
                      <img
                        src={post.user.picture}
                        alt=""
                        className="gaming_streamer_pic"
                      />
                      <div className="gaming_live_text">
                        <h4>{post.gamingData?.title || post.text}</h4>
                        <span className="gaming_streamer_name">
                          {post.user.first_name} {post.user.last_name}
                        </span>
                        <span className="gaming_game_name">{post.gamingData?.game}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* All Content Grid */}
        <section className="gaming_content_section">
          <div className="gaming_section_header">
            <h2>
              {category === "all"
                ? "Conteúdo Popular"
                : categories.find((c) => c.id === category)?.name}
            </h2>
          </div>

          {posts.length === 0 ? (
            <div className="gaming_feed_empty">
              <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
              </svg>
              <h3>Nenhum conteúdo encontrado</h3>
              <p>Seja o primeiro a compartilhar seu gameplay!</p>
            </div>
          ) : (
            <>
              <div className="gaming_grid">
                {posts
                  .filter((p) => category === "all" || !p.gamingData?.isLive)
                  .map((post) => (
                    <div
                      key={post._id}
                      className="gaming_card"
                      onClick={() => setSelectedStream(post)}
                    >
                      <div className="gaming_card_thumbnail">
                        {post.videos?.[0] ? (
                          <video src={post.videos[0].url || post.videos[0]} muted />
                        ) : post.images?.[0] ? (
                          <img src={post.images[0].url || post.images[0]} alt="" />
                        ) : (
                          <div className="gaming_no_thumb">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                              <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                            </svg>
                          </div>
                        )}
                        {post.gamingData?.duration && (
                          <span className="gaming_duration">
                            {formatDuration(post.gamingData.duration)}
                          </span>
                        )}
                        {post.gamingData?.streamType && (
                          <span className={`gaming_type_badge ${post.gamingData.streamType}`}>
                            {categories.find((c) => c.id === post.gamingData.streamType)?.icon}{" "}
                            {categories.find((c) => c.id === post.gamingData.streamType)?.name}
                          </span>
                        )}
                        <div className="gaming_card_overlay">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="gaming_card_info">
                        <Link
                          to={`/profile/${post.user.username}`}
                          className="gaming_card_avatar"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img src={post.user.picture} alt="" />
                        </Link>
                        <div className="gaming_card_text">
                          <h4>{post.gamingData?.title || post.text}</h4>
                          <Link
                            to={`/profile/${post.user.username}`}
                            className="gaming_card_author"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {post.user.first_name} {post.user.last_name}
                          </Link>
                          <div className="gaming_card_meta">
                            {post.gamingData?.game && (
                              <span className="gaming_card_game">{post.gamingData.game}</span>
                            )}
                            <span>•</span>
                            <span>{formatViews(post.views || 0)} views</span>
                            <span>•</span>
                            <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {hasMore && (
                <div className="gaming_load_more">
                  <button onClick={loadMore} disabled={loading}>
                    {loading ? "Carregando..." : "Ver mais"}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {/* Video Player Modal */}
      {selectedStream && (
        <div className="gaming_player_overlay" onClick={() => setSelectedStream(null)}>
          <div className="gaming_player_modal" onClick={(e) => e.stopPropagation()}>
            <button className="gaming_player_close" onClick={() => setSelectedStream(null)}>
              ✕
            </button>
            <div className="gaming_player_video">
              {selectedStream.videos?.[0] ? (
                <video
                  src={selectedStream.videos[0].url || selectedStream.videos[0]}
                  controls
                  autoPlay
                />
              ) : selectedStream.images?.[0] ? (
                <img
                  src={selectedStream.images[0].url || selectedStream.images[0]}
                  alt=""
                />
              ) : (
                <div className="gaming_player_placeholder">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                    <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                </div>
              )}
            </div>
            <div className="gaming_player_info">
              <div className="gaming_player_header">
                <Link
                  to={`/profile/${selectedStream.user.username}`}
                  className="gaming_player_author"
                >
                  <img src={selectedStream.user.picture} alt="" />
                  <div>
                    <span className="gaming_player_name">
                      {selectedStream.user.first_name} {selectedStream.user.last_name}
                    </span>
                    {selectedStream.gamingData?.game && (
                      <span className="gaming_player_game">
                        Jogando {selectedStream.gamingData.game}
                      </span>
                    )}
                  </div>
                </Link>
                <button className="gaming_follow_btn">Seguir</button>
              </div>
              <h2>{selectedStream.gamingData?.title || selectedStream.text}</h2>
              {selectedStream.gamingData?.description && (
                <p className="gaming_player_desc">{selectedStream.gamingData.description}</p>
              )}
              <div className="gaming_player_stats">
                <span>{formatViews(selectedStream.views || 0)} visualizações</span>
                <span>•</span>
                <Moment fromNow locale="pt-br">{selectedStream.createdAt}</Moment>
              </div>
              {selectedStream.gamingData?.platform && (
                <div className="gaming_player_platform">
                  <span>Plataforma: {selectedStream.gamingData.platform}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
